<template>
  <div class="pa-0">

    <!-- import -->

    <div class="py-3 px-6 ma-2 layout-column font-weight-regular subtitle-2 elevation-3"
         v-if="activities && activities.import && activities.import.new && !activities.import.isInvalidDate && userIsPhysicianOrEmployee"
         v-bind:style="{'background-color': compColor.context.background}"
         style="border-radius: 3px;"
         v-bind:class="[compColor.widget.text]">

      <!-- headline -->

      <div class="font-weight-bold subtitle-1 my-1" style="line-height: 1.0rem;">{{ $t('ACTIVITIES.IMPORT') }}</div>
      <v-divider class="mb-2" v-bind:class="[compColor.divider]"></v-divider>

      <!-- Content elements -->

      <div class="layout-row" v-if="activities.import.new">
        <span class="mr-1 flex-gt-sm-40">{{ $t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.CREATE') }}:</span>
        <span class="flex">{{ activities.import.new | formatDate('DD.MM.YYYY HH:mm') }}</span>
        <div :style="{color:activities.import.newcolor }" class="font-weight-regular caption layout-align-center-end"><span style="font-weight: bold;">{{ activities.import.newdateDiff }}</span></div>
      </div>

      <div class="layout-row mb-2" v-if="activities.import.update">
        <span class="mr-1 flex-gt-sm-40">{{ $t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.LAST_UPDATE') }}:</span>
        <span class="flex">{{ activities.import.update | formatDate('DD.MM.YYYY HH:mm') }}</span>
        <div :style="{color:activities.import.updatecolor }" class="font-weight-regular caption layout-align-center-end"><span style="font-weight: bold;">{{ activities.import.updatedateDiff }}</span></div>
      </div>

      <div>
        <pie-chart
          style="height: 100px;"
          class="my-4"
          v-if="chartImportLoaded"
          :chartdata="chartImportData"
          :options="chartImportOptions"/>
      </div>

      <div class="layout-column mt-3" v-if="activities.import.updatedateDiffCount > daysLimitToShowImport">
        <v-divider class="mb-4" v-bind:class="[compColor.divider]"></v-divider>
        <span style="font-style: italic;">{{ $t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.SHOW_IMPORT_TEXT', { days: daysLimitToShowImport }) }}</span>
        <v-btn :dark="compColor.isInDarkMode"
               @click="routeToImport()"
               class="font-weight-regular subtitle-2 white--text text-none py-2 my-4 flex"
               :color="compColor.primeColor"
               dense
               depressed>
          {{ $t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.SHOW_IMPORT') }}
        </v-btn>
      </div>
    </div>

    <!-- report -->

    <div class="py-3 px-6 ma-2 layout-column font-weight-regular subtitle-2 elevation-3"
         v-bind:style="{'background-color': compColor.context.background}"
         style="border-radius: 3px;"
         v-if="userIsPhysicianOrEmployee && ((processablePatientCounts.reportable && processablePatientCounts.reportable !== '-') || (processablePatientCounts.inProgress && processablePatientCounts.inProgress !== '-') || (activities && activities.report && activities.report.reportDate && !activities.report.isInvalidDate))"
         v-bind:class="[compColor.widget.text]">

      <!-- headline -->

      <div class="font-weight-bold subtitle-1 my-1" style="line-height: 1.0rem;">{{ $t('ACTIVITIES.REPORT') }}</div>
      <v-divider class="mb-2" v-bind:class="[compColor.divider]"></v-divider>

      <!-- Content elements -->

      <div class="layout-row mb-6" v-if="activities && activities.report && activities.report.reportDate && !activities.report.isInvalidDate">
        <div class="flex">
          <span class="mr-3">{{ $t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.LAST_REPORT') }}:</span>
          <span>{{ activities.report.reportDate | formatDate('DD.MM.YYYY HH:mm') }} ({{ activities.report.reportedPatients }} {{ activities.report.reportedPatients > 1 ? $t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.PATIENTS') : $t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.PATIENT') }})</span>
        </div>

        <div :style="{color: activities.report.color }" class="font-weight-regular caption layout-align-center-end"><span style="font-weight: bold;">{{ activities.report.dateDiff }}</span></div>
      </div>

      <div style="font-style: italic; font-weight: bold"
           v-if="(processablePatientCounts.reportable && processablePatientCounts.reportable !== '-') || (processablePatientCounts.inProgress && processablePatientCounts.inProgress !== '-')"
      >{{ $t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.HEADLINE_FORMS') }}</div>

      <div v-if="(processablePatientCounts.reportable && processablePatientCounts.reportable !== '-') || (processablePatientCounts.inProgress && processablePatientCounts.inProgress !== '-')">
        <pie-chart
          style="height: 100px;"
          class="my-4"
          v-if="chartReportLoaded"
          :chartdata="chartReportData"
          :options="chartReportOptions"/>
      </div>

      <div class="layout-column mt-3" v-if="processablePatientCounts.reportable && processablePatientCounts.reportable !== '-'">
        <v-divider class="mb-4" v-bind:class="[compColor.divider]"></v-divider>

        <span style="font-style: italic;" v-if="processablePatientCounts.reportable > 1">{{ $t('COMPONENT_DASHBOARD.ACTIVITY_SHOW_REPORT_TEXT', { forms: processablePatientCounts.reportable }) }}</span>
        <span style="font-style: italic;" v-if="processablePatientCounts.reportable === 1">{{ $t('COMPONENT_DASHBOARD.ACTIVITY_SHOW_REPORT_TEXT_SINGLE') }}</span>

        <v-btn :dark="compColor.isInDarkMode"
               @click="showReportDialog = true"
               v-if="processablePatientCounts.reportable"
               class="font-weight-regular subtitle-2 white--text text-none py-2 my-4 flex"
               :color="compColor.primeColor"
               dense
               depressed>
          {{ $t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.REPORT_BUTTON') }}
        </v-btn>
      </div>
    </div>

    <report-dialog
      :showReportDialog="showReportDialog"
      @set-show-report-dialog="setShowReportDialog">
    </report-dialog>
  </div>
</template>

<script>
import PieChart from '../../../components/charts/PieChart'
import moment from 'moment'

import ReportDialog from '../../../components/documentation/ReportDialog'

function toChartData (states, data) {
  const dataset = {
    label: 'Dataset 1',
    data: data,
    backgroundColor: states.map(v => v.color)
  }
  return {
    labels: states.map(v => v.label),
    datasets: [dataset]
  }
}

export default {
  name: 'Activity',
  components: { PieChart, ReportDialog },
  data () {
    return {
      activities: null,
      daysLimitToShowImport: 7,
      processablePatientCounts: {},
      chartReportLoaded: false,
      chartReportData: null,
      chartReportOptions: {
        responsive: true,
        maintainAspectRatio: false,
        stacked: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        legend: {
          position: 'right'
        }
      },
      chartImportLoaded: false,
      chartImportData: null,
      chartImportOptions: {
        responsive: true,
        maintainAspectRatio: false,
        stacked: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        legend: {
          position: 'right'
        }
      },
      showReportDialog: false,
      importStates: [
        { label: this.$t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.CHART.LABEL_TOTAL'), color: '#F8D058' }, // 0
        { label: this.$t('COMPONENT_DASHBOARD.ACTIVITIES.IMPORT.CHART.LABEL_CURRENT'), color: '#94D95F' }, // 1
      ],
      reportStates: [
        { label: this.$t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.CHART.LABEL_IN_PROCESS'), color: '#F8D058' }, // 0
        { label: this.$t('COMPONENT_DASHBOARD.ACTIVITIES.REPORT.CHART.LABEL_READY'), color: '#94D95F' }, // 1
      ]
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    getData () {
      this.processablePatientCounts = {}
      this.activities = null

      this.$http.get('/dashboard/activities')
        .then(res => {
          if (res.data) {
            this.activities = res.data

            this.setColorAndDateDiff()

            // load import chart
            if (res.data.import && res.data.import.total > 0) {
              const current = res.data.import.current || 0
              const data = [
                current,
                res.data.import.total - current
              ]
              this.chartImportData = toChartData(this.importStates, data)
              this.chartImportLoaded = true
            }
          }
        })
        .catch(this.NotifyError)

      this.$http.get('/dashboard/forms')
        .then(res => {
          this.chartReportLoaded = false

          if (res.data) {
            this.processablePatientCounts = {
              reportable: res.data.reportable || '-',
              inProgress: res.data.inProgress || '-'
            }

            // load report chart
            const data = [
              res.data.inProgress || 0,
              res.data.reportable || 0
            ]

            this.chartReportData = toChartData(this.reportStates, data)
            this.chartReportLoaded = true
          }
        })
        .catch(this.NotifyError)
    },

    /**
     *
     */
    setColorAndDateDiff () {
      if (this.activities?.report?.reportDate) {
        this.getColorAndDateDiff(this.activities.report.reportDate, this.activities.report)
      }

      if (this.activities?.import?.new) {
        this.getColorAndDateDiff(this.activities.import.new, this.activities.import, 'new')
      }

      if (this.activities?.import?.update) {
        this.getColorAndDateDiff(this.activities.import.update, this.activities.import, 'update')
      }
    },

    /**
     * Return date-element and color for activity
     *
     * @param date The selected date of activity
     * @param value The activity
     * @param prefix The prefix for special import value
     */
    getColorAndDateDiff (date, value, prefix) {
      prefix = prefix || ''

      // set day diff

      const today = moment()
      const valueDate = moment(date)
      let diff = Math.round(moment.duration(today.diff(valueDate)).asDays())
      value[prefix + 'dateDiffCount'] = diff

      if (diff > 1) {
        value[prefix + 'dateDiff'] = this.$t('COMPONENT_DASHBOARD.ACTIVITY_DAYS_BEFORE', { days: diff })
      } else if (diff === 1 && today.day() !== valueDate.day()) { // make sure that diff is other weekday
        value[prefix + 'dateDiff'] = this.$t('COMPONENT_DASHBOARD.ACTIVITY_YESTERDAY')
      } else {
        value[prefix + 'dateDiff'] = this.$t('COMPONENT_DASHBOARD.ACTIVITY_TODAY')
      }

      // set color

      value[prefix + 'color'] = '#FA8072'

      if (diff < 30) value[prefix + 'color'] = '#F8D058'
      if (diff < 14) value[prefix + 'color'] = '#94D95F'

      // Be ensure that date is valid to show in dashboard

      if (prefix === 'new' || !prefix) {
        value.isInvalidDate = valueDate.year() === 1
      }
    },

    /**
     * Open or close modal with report dialog
     */
    setShowReportDialog () {
      this.showReportDialog = !this.showReportDialog

      if (!this.showReportDialog) {
        this.getData()
      }
    },

    routeToImport () {
      this.addTabToStore({
        routeName: 'Import',
        options: {
          headline: 'Import',
          icon: 'mdi-file-import'
        }
      })
    }
  },
  watch: {
    '$route' (to) {
      if (to.name === 'Dashboard') {
        this.getData()
      }
    }
  },
  computed: {
    userIsPhysicianOrEmployee () {
      return this.authStore.getters.userIsPhysicianOrEmployee
    }
  }
}
</script>
